import type { ClientContextData } from "~/contexts/client-context";

interface GtmSdkParams {
  playerID?: string;
  eventTime: Date;
  screenName?: ScreenName;
}
type AccessControl = { isAuthenticated?: boolean };

type PaymentProps = Pick<
  ClientContextData,
  "paymentMethod" | "amount" | "errorMessage" | "transactionId" | "statusCode"
>;

type WindowType = Pick<ClientContextData, "windowType">;
type ScreenName = "mainPage" | "logIn" | "signUp" | string;
type ScreenProps = {
  screenNumber?: string;
  screenQuestion?: string;
  screenAnswer?: string;
};
// EventTrigger название кнопки (раздела) кликнутое в хедере. например :features, casino, series...
// Используется для передачи в buttonName
type EventTrigger = { triggerId: string };

// EventTarget - раздел на который кликнул пользователь в выпадающием списке. используется для передачи в value
type EventTarget = {
  targetId: string;
};
type AppData = GtmSdkParams &
  PaymentProps &
  AccessControl &
  WindowType &
  EventTrigger;
export class GtmSdk {
  constructor(private readonly dataLayer: DataLayer) {}
  private formatDate(date: Date): string {
    return date.toISOString();
  }

  private getPlayerID(playerID?: string): string {
    return playerID ?? "0";
  }

  public callEvent(data: object, appData: AppData): void {
    let dataString = JSON.stringify(data);

    dataString = dataString.replace(
      /{{eventTimestamp}}/g,
      this.formatDate(appData.eventTime),
    );

    dataString = dataString.replace(
      /{{playerID}}/g,
      this.getPlayerID(appData.playerID),
    );

    dataString = dataString.replace(
      /{{isAuthenticated}}/g,
      appData.isAuthenticated === true ? "logged_in" : "logged_out",
    );

    dataString = dataString.replace(/{{amount}}/g, appData.amount ?? "");

    dataString = dataString.replace(/{{triggerId}}/g, appData.triggerId);

    dataString = dataString.replace(
      /{{windowType}}/g,
      appData.windowType ?? "",
    );

    dataString = dataString.replace(
      /{{errorMessage}}/g,
      appData.errorMessage ?? "",
    );

    dataString = dataString.replace(
      /{{paymentMethod}}/g,
      appData.paymentMethod ?? "",
    );

    dataString = dataString.replace(
      /{{transactionId}}/g,
      appData.transactionId ?? "",
    );

    dataString = dataString.replace(
      /{{statusCode}}/g,
      appData.statusCode ?? "",
    );

    const updatedDataObject: Record<string, string> = JSON.parse(dataString);

    this.dataLayer.push(updatedDataObject);
  }

  // клик на кнопку Log In на лендинге (либо в хэдере, либо в центре страницы)
  public loginHeader(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "logIn",
      eventLocation: "landing",
      eventAction: "click",
      eventLabel: "logInHeader",
      eventTime: this.formatDate(params.eventTime),
      screenName: params.screenName,
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // клик на кнопку Create Account на лендинге (либо в хэдере, либо в центре страницы)
  public createAccountHeader(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "registration",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "landing",
      eventAction: "click",
      eventLabel: "createAccountHeader",
      screenName: params.screenName,
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // Клик на одну из кнопок в хедере (не учитывая выбор языка, логин, регистрация). То есть то что находится в левой части хедера.
  public clickHeaderMain(params: GtmSdkParams & EventTrigger): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "all",
      eventAction: "click",
      eventLocation: "landing",
      eventLabel: "clickHeaderMain",
      eventTime: this.formatDate(params.eventTime),
      PlayerID: this.getPlayerID(params.playerID),
      buttonName: params.triggerId,
    });
  }

  // После клика на одну из кнопок в хедера появляется либо выпадающий список либо редиректит на новый юрл. Это события должно тригерится при нажатии на один из разделов с выпадающим списком.
  public clickHeaderSub(
    params: GtmSdkParams & EventTrigger & EventTarget,
  ): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "all",
      eventAction: "click",
      eventLocation: "landing",
      eventLabel: "clickHeaderSub",
      eventTime: this.formatDate(params.eventTime),
      PlayerID: this.getPlayerID(params.playerID),
      buttonName: params.triggerId,
      value: params.targetId,
    });
  }

  // клик на кнопку Log In на лендинге (либо в хэдере, либо в центре страницы)
  public logInMain(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "logIn",
      eventLocation: "landing",
      eventAction: "click",
      eventLabel: "logInMain",
      screenName: params.screenName,
      PlayerID: this.getPlayerID(params.playerID),
      eventTime: this.formatDate(params.eventTime),
    });
  }

  // клик на кнопку Create Account на лендинге (либо в хэдере, либо в центре страницы)
  public createAccountMain(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "registration",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "landing",
      eventAction: "click",
      eventLabel: "createAccountMain",
      screenName: params.screenName,
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // клик на кнопку Play Now
  public playNow(params: GtmSdkParams & AccessControl): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "all",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "landing",
      eventAction: "click",
      eventLabel: "playNow",
      value: params.isAuthenticated ? "logged_in" : "logged_out",
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // выбор страны
  public submitCountry(params: GtmSdkParams & ScreenProps): void {
    this.dataLayer.push({
      event: "event",
      eventAction: "landing",
      eventLabel: "event",
      screenNumber: params.screenNumber,
      screenQuestion: params.screenQuestion,
      PlayerID: this.getPlayerID(params.playerID),
      ...params,
      eventTime: this.formatDate(params.eventTime),
    });
  }

  public userId(params: GtmSdkParams & { playerID: string }): void {
    const userID = Number(params.playerID);

    this.dataLayer.push({
      event: "user_id",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "public",
      user_id: Number.isNaN(userID) ? params.playerID : userID,
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  public signUp(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "registration",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "landing",
      eventAction: "event",
      eventLabel: "signUp",
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // событие логина
  public logIn(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventFlow: "logIn",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "landing",
      eventAction: "event",
      eventLabel: "logIn",
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // событие для поиска причины не прохождения FTD флоу после выбора страны
  public luckboxLoadPromotionsLoadingFailed(
    params: GtmSdkParams & { method?: string },
  ): void {
    this.dataLayer.push({
      event: "event",
      eventCategory: "campaign",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "public",
      eventAction: "event",
      eventLabel: "luckbox_load_promotions_loading_failed",
      PlayerID: this.getPlayerID(params.playerID),
      method: params.method,
    });
  }

  // событие для поиска индикации успешного прохождения ftd-flow
  public luckboxLoadPromotionsLoadingSuccess(
    params: GtmSdkParams & { method?: string },
  ): void {
    this.dataLayer.push({
      event: "event",
      eventCategory: "campaign",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "public",
      eventAction: "event",
      eventLabel: "luckbox_load_promotions_loading_success",
      PlayerID: this.getPlayerID(params.playerID),
      method: params.method,
    });
  }

  // Показ попапа с установкой приложения (МС OTP-registration)
  public popupInstall(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventCategory: "campaign",
      eventAction: "event",
      eventTime: this.formatDate(params.eventTime),
      eventLocation: "public",
      eventLabel: "popupInstall",
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // Клик на кнопку Download App внутри попапа (МС OTP-registration)
  public clickDownloadApp(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventCategory: "all",
      eventAction: "click",
      eventLocation: "landing",
      eventLabel: "clickDownloadApp",
      button: "download",
      eventTime: this.formatDate(params.eventTime),
      PlayerID: this.getPlayerID(params.playerID),
    });
  }

  // Клик на кнопку Play in web внутри попапа (МС OTP-registration)
  public clickPlayInWeb(params: GtmSdkParams): void {
    this.dataLayer.push({
      event: "event",
      eventCategory: "all",
      eventAction: "click",
      eventLocation: "landing",
      eventLabel: "clickPlayinWeb",
      button: "playInWeb",
      eventTime: this.formatDate(params.eventTime),
      PlayerID: this.getPlayerID(params.playerID),
    });
  }
}
